import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import FormFields from "./components/FormFields";

import { getOneRevendeurApi, updateRevendeurApi } from "api/superAdmin";

function EditRevendeur() {
  const { revendeurId } = useParams();
  const navigate = useNavigate();

  const [originalRevendeurData, setOriginalRevendeurData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRevendeurData = async () => {
      try {
        setIsLoading(true);
        const data = await getOneRevendeurApi(revendeurId);

        setOriginalRevendeurData(data);
        setFormData(data);
        setIsLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error fetching revendeur data for edit:", err);
        setError(err);
        setIsLoading(false);
        toast.error("Une erreur s'est produite lors de la récupération des données du revendeur.");
      }
    };

    if (revendeurId) {
      fetchRevendeurData();
    } else {
      console.error("Revendeur ID is missing in URL");
      setError(new Error("L'ID du revendeur est manquant dans l'URL."));
      setIsLoading(false);
    }
  }, [revendeurId]);

  const handleFormUpdate = (formFieldsProp) => {
    setFormData(formFieldsProp);
  };

  const handleSaveChanges = async () => {
    try {
      const dataToUpdate = {
        ...formData,

        eventID: formData?.eventID ?? null,
        fromProduced: formData?.fromProduced ?? null,
      };

      await updateRevendeurApi(revendeurId, dataToUpdate);

      Swal.fire({
        title: "Mise à jour effectuée",
        text: "Les modifications ont été enregistrées avec succès.",
        icon: "success",
      });

      navigate(`/revendeurs/voir`);
    } catch (err) {
      console.error("Error saving revendeur changes:", err.response?.data?.error || err.message);
      toast.error(
        err.response?.data?.error ||
          "Une erreur s'est produite lors de l'enregistrement des modifications."
      );
    }
  };

  if (isLoading || !formData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5" color="text">
            Chargement des données du revendeur...
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5" color="error">
            Erreur lors de la récupération des données: {error.message}
          </MDTypography>
        </MDBox>
        <Footer />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox mb={6}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MDTypography variant="h4" fontWeight="medium">
                Modifier le Revendeur
              </MDTypography>

              <MDBox mt={1} mb={2}>
                <MDTypography variant="body2" color="text"></MDTypography>
              </MDBox>
            </Grid>

            <Grid item xs={12} lg={6}>
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="info" onClick={handleSaveChanges}>
                  Enregistrer les modifications
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormFields initialData={originalRevendeurData} formValues={handleFormUpdate} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </DashboardLayout>
  );
}

export default EditRevendeur;
